/**
 * Self-refresh echo suppression (#520).
 *
 * Every mutation used to refresh the active tab twice: once from `run()`'s own
 * refresh, and again ~650ms later when the same mutation's household event came
 * back over SSE. The fix is to record the scope a write just refreshed and drop
 * the realtime echo of *that user's own* change to *that same scope* for a short
 * window afterwards.
 *
 * Dropping an event is not free: the realtime cursor advances past it, so a
 * dropped echo is never replayed. Anything here that decides to stop vouching
 * for a scope therefore has to ask whether it is giving up on an echo that has
 * already been consumed, and if so queue one corrective refresh — see
 * `settleSwallowedEcho` and `takeSelfRefreshCorrections`.
 *
 * The bookkeeping lives here rather than inline in App.tsx so the rules that
 * decide whether an event is dropped are testable without mounting the app. The
 * two things that stay in App.tsx are resolving the `"active"` scope to the
 * current tab, which needs the live tab ref, and actually performing a queued
 * corrective refresh.
 */

/**
 * How long a scope stays marked. Sized to cover the server's 250ms change-bus
 * delay plus the client's 650ms realtime debounce with room to spare. The mark
 * is re-armed the moment the write resolves and again when its refresh
 * finishes, so the window is measured from the commit rather than from the
 * start of a possibly slow action.
 */
export const selfRefreshEchoMs = 2500;

/** The parts of a household realtime event the drop rules look at. */
export type SelfRefreshEvent = {
  self?: boolean;
  authChanged?: boolean;
  refreshAuth?: boolean;
  /**
   * The client-chosen id of the write this event announces (#800), when there
   * was a browser behind it. Absent for an agent tool run, a script, or a client
   * too old to send one.
   */
  writeId?: string;
};

/**
 * How many of this session's own write ids to remember.
 *
 * An echo arrives within a couple of seconds of its write, so this only has to
 * outlive that. It is a ring, not a leak: the oldest id is forgotten once the
 * cap is reached, and forgetting one is safe in the direction that matters —
 * an unrecognised id is treated as somebody else's and always refreshes.
 */
const OWN_WRITE_MEMORY = 200;

/**
 * A live mark: when it expires, and which arming owns it. The owner is an
 * opaque id rather than the expiry so two writes that land in the same
 * millisecond cannot be mistaken for each other.
 */
export type SelfRefreshMark = { until: number; owner: number };

/**
 * The suppression bookkeeping for one app instance.
 *
 * `live` is keyed by scope rather than being a single slot so two concurrent
 * writes on different scopes (a background chore refresh overlapping a grocery
 * save) cannot clobber each other's mark.
 *
 * `newestCycle` is the other half of that: two writes on the *same* scope do
 * share a slot, so each cycle records its id here when it opens and every later
 * write on that scope supersedes it. A refresh that finishes after a newer
 * cycle has already opened is no longer authoritative for the scope — see
 * `refreshed()` — and this map is how it finds that out.
 *
 * `swallowedEchoes` is the debt ledger. Dropping an echo is a *bet* that some
 * refresh covers the change it announced; the realtime cursor advances past a
 * dropped event, so if that bet is wrong nothing replays it. Each drop records
 * the id it happened at, and every cycle checks that id when it settles.
 *
 * `corrections` is the queue that pays the debt off: scopes that need one
 * refresh that no cycle is going to perform. It is a Set, so overlapping cycles
 * asking at once collapse into a single refresh.
 */
export type SelfRefreshMarks = {
  readonly live: Map<string, SelfRefreshMark>;
  readonly newestCycle: Map<string, number>;
  readonly swallowedEchoes: Map<string, number>;
  readonly corrections: Set<string>;
  /** Tries spent on the currently-queued correction per scope; see `requeueSelfRefreshCorrection`. */
  readonly correctionAttempts: Map<string, number>;
  /**
   * Every write id THIS session has issued, newest last (#800).
   *
   * What makes `self` per-session instead of per-user. An echo naming a write
   * this tab never made belongs to the same account on another tab or device,
   * and must always refresh — a fact the server's `self` flag cannot express,
   * because from its side both are the same human.
   */
  readonly ownWrites: Set<string>;
  /**
   * The subset of `ownWrites` that no cycle has claimed — the candidates a
   * claim may draw from. Where a claimed write is *considered covered* is
   * `coveringScopes` below; this set only says "nobody has taken it yet".
   *
   * This is the fix for #800 itself. A `run(..., { refresh: false })` write
   * opens no cycle, so nothing ever moves its id out of here, so its echo is
   * never suppressed no matter what mark happens to be live on the scope. A
   * write inside a cycle leaves as that cycle commits — a refresh is on its way
   * that will cover it — and comes back if that refresh never lands.
   */
  readonly uncoveredWrites: Set<string>;
  /**
   * For each claimed write, the scopes whose refresh actually covers it (#800,
   * codex review).
   *
   * Coverage is per-scope because a refresh only re-reads one scope. A cycle
   * claims by *timing* — it cannot know which resource a write touched — so a
   * concurrently-issued write on another scope can be the one it picks up. If
   * that made the write covered everywhere, its echo would be suppressed on the
   * scope that never refreshed, leaving that screen stale: the very failure this
   * issue is about, moved one scope over.
   *
   * Recording the claiming scope makes a mis-claim harmless. The echo is
   * suppressed only where a refresh really happened; anywhere else the write
   * still reads as `uncovered` and refreshes. The worst case becomes one
   * redundant refresh, never stale data.
   */
  readonly coveringScopes: Map<string, Set<string>>;
  nextId: number;
  readonly notifyCorrection?: () => void;
};

/** Total attempts allowed for one queued correction before it is given up on. */
const MAX_CORRECTION_ATTEMPTS = 3;

/**
 * `notifyCorrection` is called when a scope is newly queued for a corrective
 * refresh; it means "there is work, come and drain me", not "refresh now", so
 * the host can coalesce a burst into one pass. Optional: a caller that never
 * drains simply keeps the old fail-open behaviour.
 */
export function createSelfRefreshMarks(notifyCorrection?: () => void): SelfRefreshMarks {
  return {
    live: new Map(),
    newestCycle: new Map(),
    swallowedEchoes: new Map(),
    corrections: new Set(),
    correctionAttempts: new Map(),
    ownWrites: new Set(),
    uncoveredWrites: new Set(),
    coveringScopes: new Map(),
    nextId: 0,
    notifyCorrection
  };
}

/**
 * Record that this session just issued a write (#800).
 *
 * Called by the api client for every mutation it sends, before the response is
 * back — the echo can be delivered while the write is still in flight, exactly
 * like the scope mark it sits beside.
 *
 * The id starts *uncovered*: nothing has refreshed for it yet. If it is inside a
 * `run()` cycle, that cycle's `commit()` claims it moments later. If it is a
 * `{ refresh: false }` write, nothing ever claims it — which is precisely why
 * its echo stops being swallowed.
 */
export function noteWriteIssued(marks: SelfRefreshMarks, writeId: string): void {
  if (!writeId) return;
  // Re-inserting moves it to the end of the Set's iteration order only if it is
  // deleted first, which is what keeps the ring honest about which id is oldest.
  marks.ownWrites.delete(writeId);
  marks.ownWrites.add(writeId);
  marks.uncoveredWrites.add(writeId);
  // A write going out is uncovered by definition, so an id being re-issued does
  // not inherit the coverage of its predecessor.
  marks.coveringScopes.delete(writeId);
  while (marks.ownWrites.size > OWN_WRITE_MEMORY) {
    const oldest = marks.ownWrites.values().next().value;
    if (oldest === undefined) break;
    marks.ownWrites.delete(oldest);
    marks.uncoveredWrites.delete(oldest);
    marks.coveringScopes.delete(oldest);
  }
}

/**
 * How this event's write relates to what this session has refreshed for.
 *
 * - `foreign` — a write this session did not make (or one it has forgotten).
 *   Always refresh: this is the same-account-another-tab case that `self` alone
 *   could not see.
 * - `uncovered` — ours, and no refresh of *this scope* covers it. Always
 *   refresh: this is #800.
 * - `covered` — ours, and a refresh of this scope has covered it or is on its
 *   way. The ordinary #520 echo; the mark rules decide.
 * - `unattributed` — the event names no write at all (agent, script, old
 *   client). The mark rules decide, exactly as before.
 *
 * `scope` is what makes coverage answerable rather than a guess. A cycle claims
 * writes by timing, so it can pick up one belonging to another screen; asking
 * "did a refresh of *this* scope cover it" turns that mis-claim into an extra
 * refresh instead of a swallowed echo (codex review). Omit it to ask the weaker
 * question — has anything claimed this write at all.
 */
export type WriteCoverage = "foreign" | "uncovered" | "covered" | "unattributed";

export function writeCoverageOf(
  marks: SelfRefreshMarks,
  event: SelfRefreshEvent,
  scope?: string
): WriteCoverage {
  const writeId = event.writeId;
  if (!writeId) return "unattributed";
  if (!marks.ownWrites.has(writeId)) return "foreign";
  const covering = marks.coveringScopes.get(writeId);
  if (!covering || covering.size === 0) return "uncovered";
  if (scope === undefined) return "covered";
  return covering.has(scope) ? "covered" : "uncovered";
}

function takeId(marks: SelfRefreshMarks): number {
  marks.nextId += 1;
  return marks.nextId;
}

/**
 * Queue one corrective refresh for `scope`. Repeats before the queue is drained
 * are absorbed, so N overlapping cycles discovering the same swallowed echo
 * cost one refresh, not N.
 */
function requestCorrection(marks: SelfRefreshMarks, scope: string): void {
  if (marks.corrections.has(scope)) return;
  // A fresh debt, so the retry budget starts over: this is a different swallowed
  // echo from whatever may have exhausted the budget earlier.
  marks.correctionAttempts.delete(scope);
  marks.corrections.add(scope);
  marks.notifyCorrection?.();
}

/**
 * Drain the corrective-refresh queue. The host refreshes each scope returned
 * with a plain refresh — no cycle, no mark — so a correction cannot arm a mark
 * that swallows the next legitimate echo, and cannot queue another correction
 * of its own.
 */
export function takeSelfRefreshCorrections(marks: SelfRefreshMarks): string[] {
  const scopes = [...marks.corrections];
  marks.corrections.clear();
  return scopes;
}

/**
 * Put a drained correction back after its refresh failed. The echo this was
 * paying off is already consumed and will not replay, so dropping the scope on a
 * transient error would leave the tab stale until the user navigates — the exact
 * hole corrections exist to close.
 *
 * Bounded: `MAX_CORRECTION_ATTEMPTS` total tries per scope, because the retry is
 * driven by the same failure it is retrying. A scope that keeps failing is an
 * outage, not a race, and the caller has already surfaced the error; retrying
 * forever would just add requests to a server that is already refusing them. The
 * attempt count clears as soon as a correction is queued afresh by a new
 * swallowed echo, so a later, unrelated correction always gets its full budget.
 */
export function requeueSelfRefreshCorrection(marks: SelfRefreshMarks, scope: string): boolean {
  const attempts = (marks.correctionAttempts.get(scope) ?? 1) + 1;
  if (attempts > MAX_CORRECTION_ATTEMPTS) {
    marks.correctionAttempts.delete(scope);
    return false;
  }
  marks.correctionAttempts.set(scope, attempts);
  marks.corrections.add(scope);
  marks.notifyCorrection?.();
  return true;
}

/** How a cycle finished, from the point of view of the data now on screen. */
type Settlement =
  /** Its refresh landed and it is still the newest cycle: it speaks for the scope. */
  | "sealed"
  /** Its refresh landed but a newer cycle has opened since: the payload may be a write behind. */
  | "superseded"
  /** Nothing landed — the action or the refresh threw. */
  | "failed";

/**
 * Settle this cycle against any echo the scope swallowed, and queue a
 * corrective refresh when the drop can no longer be repaired by waiting.
 *
 * `startId` is the id the cycle armed with immediately before its refresh went
 * out (the `commit()` re-arm for `run()`, the opening mark for
 * `refreshAfterOwnChange`), so it orders the request against the drop on the
 * same monotonic counter the marks already use — no timestamps, no clock skew.
 *
 * - `swallowed < startId` — the request went out *after* the dropped event
 *   arrived, so the write it announced had already committed and the response
 *   contains it. A response that landed pays the debt off; one that never
 *   landed leaves it for whoever settles next.
 * - `swallowed > startId` — the drop happened while this request was in flight,
 *   so this response may predate the change entirely. What to do depends on
 *   what else is true:
 *   - `sealed`: leave it. This is the ordinary #520 case — the cycle committed
 *     its own write, refreshed, and is now swallowing that write's own echo, so
 *     correcting here would restore the double refresh this whole module
 *     removes. The debt stays outstanding, and because ids only go up it can
 *     never make a *later* cycle correct: only a cycle already in flight when
 *     the drop happened can have a `startId` below it.
 *   - `superseded` / `failed`: correct. Deleting the mark ("fail open") only
 *     helps an echo that has not arrived yet; this one already has, and the
 *     realtime cursor moved past it, so nothing is coming to fix the screen.
 *
 * Queueing a correction does *not* discharge the debt, only a landed refresh
 * issued after the drop does. Three stale responses can be racing one drop, and
 * if one of them lands after the corrective refresh has already gone out it has
 * to be able to ask for another one. That cannot run away: only cycles that
 * were already in flight when the drop happened can have a `startId` below it,
 * every cycle settles exactly once, and a correction opens no cycle of its own.
 */
function settleSwallowedEcho(
  marks: SelfRefreshMarks,
  scope: string,
  startId: number,
  settlement: Settlement
): void {
  const swallowed = marks.swallowedEchoes.get(scope);
  if (swallowed === undefined) return;
  if (swallowed < startId) {
    if (settlement !== "failed") marks.swallowedEchoes.delete(scope);
    return;
  }
  if (settlement === "sealed") return;
  requestCorrection(marks, scope);
}

/**
 * Record that `scope` was just refreshed because of a change this user made.
 * Returns the mark's owner id, which `clearSelfRefreshed` uses as a token so a
 * failed write only ever retracts its own mark.
 */
export function markSelfRefreshed(marks: SelfRefreshMarks, scope: string, now: number = Date.now()): number {
  const owner = takeId(marks);
  marks.live.set(scope, { until: now + selfRefreshEchoMs, owner });
  return owner;
}

/**
 * Retract a mark whose refresh never happened — the action threw, or it
 * committed and the refresh that was supposed to cover it threw — so the echo
 * of a write that did land is still allowed through. No-op when a newer mark
 * has replaced it: that mark belongs to a different in-flight action.
 *
 * Callers that re-arm mid-flight must pass the token of their LATEST mark, not
 * the one they opened with, or this silently does nothing.
 */
export function clearSelfRefreshed(marks: SelfRefreshMarks, scope: string, owner: number): void {
  if (marks.live.get(scope)?.owner === owner) marks.live.delete(scope);
}

/**
 * One write-then-refresh cycle's mark bookkeeping. The rules are easy to state
 * and easy to get wrong inline, so they live here where they are tested:
 *
 * - the mark opens before the write, because the echo can be delivered while
 *   the write is still in flight;
 * - `commit()` re-arms it when the write lands, so a slow action cannot let
 *   the window expire before the echo arrives, and so the cycle owns the mark
 *   again — its write is the newest thing no refresh has covered yet;
 * - `refreshed()` re-arms it once more and *seals* the cycle — the suppression
 *   has been earned by a refresh that actually happened — but only while this
 *   cycle is still the newest one opened for the scope (see below);
 * - `abandon()` retracts whatever mark the cycle still owns. It tracks the
 *   latest token, not the opening one, so a cycle that committed and then
 *   failed its refresh really does release the scope instead of no-op'ing
 *   against a token that was already replaced.
 *
 * Overlapping cycles on one scope are why `refreshed()` is conditional. Two
 * writes to the same tab can be in flight at once (a `run()` save while a
 * background chore upload refreshes, say), and their refreshes can land out of
 * order. An older cycle finishing last used to overwrite the newer cycle's
 * mark, which broke suppression in both directions: the newer cycle could no
 * longer retract its own mark when its refresh failed (the token no longer
 * matched), and the older refresh's possibly-pre-newer-write payload was left
 * on screen behind a live mark that swallowed the echo that would have fixed
 * it. Either way the tab stayed stale. So a superseded cycle does not arm
 * anything on completion; it drops the scope's mark instead, because its
 * result may have painted older data over the newer cycle's. The newest cycle
 * re-arms when it commits or when its own refresh lands, so the cost of that
 * is at most one extra refresh in a race — never stale data.
 *
 * Dropping the mark is only half of it, though, because it is *passive*: it
 * opens the door for the next echo, and the echo the screen needed may already
 * have come and gone. In the interleaving the older refresh goes out, the newer
 * cycle commits and refreshes and arms its mark, that write's event arrives and
 * is swallowed, and only then does the older, staler response land — deleting
 * the mark at that point recovers nothing, because the realtime cursor has
 * advanced and no further event is owed. So every settle also runs
 * `settleSwallowedEcho`, which queues one corrective refresh when a cycle
 * finishes without vouching for a drop that happened while it was in flight.
 *
 * The scope is resolved once, by the caller, before the cycle starts: the
 * refresh this cycle covers targets one scope, so all of its marks must too.
 */
export type SelfRefreshCycle = {
  commit: (now?: number) => void;
  /**
   * Claim the writes this cycle's refresh will cover, WITHOUT re-arming the
   * mark (#800, codex review).
   *
   * For callers whose write has already landed before the cycle opens —
   * `refreshAfterOwnChange`, which every direct-write surface uses. They never
   * call `commit()`, so without this their write stays in `uncoveredWrites` and
   * its echo is let through: a second refresh for every one of them, which is
   * precisely the #520 double refresh that #800's ledger must not reintroduce.
   *
   * Separate from `commit()` because these callers deliberately do not re-arm:
   * the write is already done, so the opening mark is the right window.
   */
  claim: () => void;
  refreshed: (now?: number) => void;
  abandon: () => void;
};

export function beginSelfRefreshCycle(
  marks: SelfRefreshMarks,
  scope: string,
  now: number = Date.now()
): SelfRefreshCycle {
  const cycleId = takeId(marks);
  marks.newestCycle.set(scope, cycleId);
  let owner = markSelfRefreshed(marks, scope, now);
  let settled = false;
  // The write ids this cycle's refresh is going to cover (#800). Claimed at
  // commit, because that is the moment the refresh is about to be issued and
  // everything issued before it is inside its answer. Handed back if the refresh
  // never lands, so their echoes go back to being worth acting on.
  // Writes that were already outstanding when this cycle opened. They belong to
  // something else — another scope's `{ refresh: false }` write, or a request
  // still in flight — and a refresh issued now cannot be trusted to contain them
  // (codex review). Draining every uncovered write would mark them covered and
  // let the mark on THEIR scope swallow their echo, leaving that screen stale:
  // the failure this issue is about, moved one scope over.
  const preexisting = new Set(marks.uncoveredWrites);
  let covering: string[] = [];
  const take = (writeId: string) => {
    marks.uncoveredWrites.delete(writeId);
    // Against THIS cycle's scope only: that is the one screen this refresh
    // re-reads, so it is the only place the write can be said to be on show.
    const scopes = marks.coveringScopes.get(writeId) ?? new Set<string>();
    scopes.add(scope);
    marks.coveringScopes.set(writeId, scopes);
    covering.push(writeId);
  };
  /** Writes issued since this cycle opened: the action's own. */
  const claimOwnWrites = () => {
    for (const writeId of [...marks.uncoveredWrites]) {
      if (!preexisting.has(writeId)) take(writeId);
    }
  };
  /**
   * The single newest outstanding write, for a caller whose write landed BEFORE
   * the cycle opened (`refreshAfterOwnChange`) — so `claimOwnWrites` would find
   * nothing of theirs. Newest rather than all, so an older pending write on
   * another scope is left uncovered for whoever really refreshes it.
   *
   * It is still a guess: no write id reaches this path, so a write issued
   * concurrently on another scope can be newer and get picked instead. That is
   * survivable only because the claim is recorded against this cycle's scope
   * (`take`) — the mis-claimed write stays uncovered on its own screen and its
   * echo still refreshes there, while ours merely loses a suppression it would
   * have got. One extra refresh, never a stale screen (codex review).
   */
  const claimLatestWrite = () => {
    const latest = [...marks.uncoveredWrites].at(-1);
    if (latest !== undefined) take(latest);
  };
  const releaseCovered = () => {
    for (const writeId of covering) {
      const scopes = marks.coveringScopes.get(writeId);
      scopes?.delete(scope);
      // Only back on the candidate list once nothing covers it anywhere;
      // another scope's cycle may still be vouching for it.
      if (scopes && scopes.size === 0) marks.coveringScopes.delete(writeId);
      if (marks.ownWrites.has(writeId) && !marks.coveringScopes.has(writeId)) {
        marks.uncoveredWrites.add(writeId);
      }
    }
    covering = [];
  };
  const superseded = () => marks.newestCycle.get(scope) !== cycleId;
  return {
    commit(at: number = Date.now()) {
      if (settled) return;
      // Unconditional, even when superseded: a write that just committed is by
      // definition not covered by any refresh that is already done, so this
      // cycle has to own the mark until its own refresh lands or fails.
      owner = markSelfRefreshed(marks, scope, at);
      claimOwnWrites();
    },
    claim() {
      if (settled) return;
      claimLatestWrite();
    },
    refreshed(at: number = Date.now()) {
      if (settled) return;
      settled = true;
      if (superseded()) {
        // A newer cycle opened for this scope while we were in flight, so this
        // refresh is no longer the authority on what is on screen. Fail open:
        // let the echo through rather than vouch for data that may already be
        // behind — and, if an echo was already swallowed while we were in
        // flight, refresh once ourselves, because "let it through" cannot
        // recover an event that is already gone.
        settleSwallowedEcho(marks, scope, owner, "superseded");
        // Same reasoning as the mark: this refresh may be a write behind, so it
        // does not get to vouch for the writes it claimed (#800). Handing them
        // back costs at most one extra refresh; keeping them would leave a write
        // this payload predates permanently unrendered.
        releaseCovered();
        marks.live.delete(scope);
        return;
      }
      settleSwallowedEcho(marks, scope, owner, "sealed");
      // The refresh landed and this cycle still speaks for the scope, so the
      // writes it claimed really are on screen. `covering` is simply dropped.
      covering = [];
      owner = markSelfRefreshed(marks, scope, at);
    },
    abandon() {
      if (settled) return;
      settled = true;
      // Nothing landed, so nothing covered these (#800).
      releaseCovered();
      // Same reasoning as the superseded branch, for the path where nothing
      // landed at all: retracting the mark only re-opens the door for the *next*
      // echo, so a drop that already happened needs a refresh of its own.
      settleSwallowedEcho(marks, scope, owner, "failed");
      clearSelfRefreshed(marks, scope, owner);
    }
  };
}

/**
 * Is this event just the echo of a change we already refreshed for? Only the
 * user's own edits qualify, only for the scope currently on screen, and only
 * while that scope's mark is still live. An auth change, an agent's write, or
 * anything another member does always falls through to a real refresh.
 */
export function isOwnRefreshedEcho(
  marks: SelfRefreshMarks,
  event: SelfRefreshEvent,
  scope: string,
  now: number = Date.now()
): boolean {
  if (!event.self || event.authChanged || event.refreshAuth) return false;
  // The correlation #800 added. Before it, the two cases below were the same
  // observation as the ordinary #520 echo — "something arrived while this scope
  // is marked" — and suppression had to guess:
  //
  //  - `foreign`: the same account writing from another tab or device. The
  //    server calls that `self` because it is the same human, and this session's
  //    mark is live for its OWN unrelated write, so it used to be swallowed.
  //  - `uncovered`: this session's write that no refresh covers, which is the
  //    `{ refresh: false }` case the issue is about. It committed inside another
  //    cycle's window, so the mark was armed — by a refresh that had already gone
  //    out and could not contain it.
  //
  // Both must be allowed through. Everything else falls to the mark rules, so
  // the #520 double refresh stays removed.
  // Asked against this scope: a refresh of some *other* screen is no reason to
  // drop an event this one still needs (codex review).
  const coverage = writeCoverageOf(marks, event, scope);
  if (coverage === "foreign" || coverage === "uncovered") return false;
  const mark = marks.live.get(scope);
  if (mark === undefined) return false;
  if (now >= mark.until) {
    marks.live.delete(scope);
    return false;
  }
  // Record the drop against the scope, not against the mark: dropping an event
  // is irreversible (the realtime cursor advances past it), so whichever cycle
  // is still in flight has to answer for it even if a later cycle has replaced
  // the mark by then. Cycles settle this in `settleSwallowedEcho`. The newest
  // drop wins — a refresh issued after it was issued after all the earlier ones
  // too, so one id is enough to decide coverage.
  marks.swallowedEchoes.set(scope, takeId(marks));
  return true;
}
